import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function ReadMoreAutismSection({ 
  className = '', 
  titleOverride = null, 
  subtitleOverride = null 
}) {
  const { lang, getLocalizedValue, localizePath } = useLanguage();
  const [cmsArticles, setCmsArticles] = useState([]);
  const [cmsPage, setCmsPage] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await fetch('/_api/public/v2/content/artikler?term=kategori:autisme&limit=3&sort=updatedAt&order=desc');
        if (res.ok) {
          const data = await res.json();
          if (data.items && data.items.length > 0) {
            setCmsArticles(data.items);
          }
        }
      } catch (err) {
        console.error('Error fetching autisme articles:', err);
      }
    };

    const fetchPage = async () => {
      try {
        const res = await fetch('/_api/public/content/autisme_side');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setCmsPage(data[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching autisme_side:', err);
      }
    };

    fetchArticles();
    if (!titleOverride || !subtitleOverride) {
      fetchPage();
    }
  }, [lang, titleOverride, subtitleOverride]);

  const defaultArticles = [
    {
      slug: 'tegn-paa-autisme-hos-voksne-symptomer-og-kendetegn',
      title: lang === 'da' 
        ? 'Tegn på autisme hos voksne – symptomer og kendetegn' 
        : 'Tecken på autism hos vuxna – symtom och kännetecken',
      desc: lang === 'da'
        ? 'Læs om typiske tegn på autisme hos voksne, herunder social kommunikation, sansefølsomhed og behov for struktur.'
        : 'Läs om typiska tecken på autism hos vuxna, inklusive social kommunikation, sensorisk känslighet och behov av struktur.'
    },
    {
      slug: 'autisme-og-maskering-hvad-er-camouflering',
      title: lang === 'da'
        ? 'Autisme og maskering – hvad er camouflering?'
        : 'Autism och maskering – vad är kamouflering?',
      desc: lang === 'da'
        ? 'Læs om maskering ved autisme, hvorfor den opstår, og hvordan den kan påvirke trivsel og hverdagsliv.'
        : 'Läs om maskering vid autism, varför det uppstår och hur det kan påverka mående och vardagsliv.'
    },
    {
      slug: 'audhd-naar-adhd-og-autisme-overlapper',
      title: lang === 'da'
        ? 'AuDHD – når ADHD og autisme overlapper'
        : 'AuDHD – när ADHD och autism överlappar',
      desc: lang === 'da'
        ? 'Læs om samspillet mellem ADHD og autisme, og hvordan de to tilstande kan komme til udtryk samtidig.'
        : 'Läs om samspelet mellan ADHD och autism, och hur de två tillstånden kan ta sig uttryck samtidigt.'
    }
  ];

  const displayArticles = cmsArticles.length > 0
    ? cmsArticles.map(art => ({
        slug: typeof art.slug === 'object' ? (art.slug[lang] || art.slug.da || Object.values(art.slug)[0]) : (art.slug || art.id),
        title: getLocalizedValue(art.titel),
        desc: getLocalizedValue(art.resume)
      }))
    : defaultArticles;

  const sectionTitle = titleOverride || (cmsPage && getLocalizedValue(cmsPage.artikler_overskrift)) || (lang === 'da' ? 'Læs mere om autisme' : 'Läs mer om autism');
  const sectionSubtitle = subtitleOverride || (cmsPage && getLocalizedValue(cmsPage.artikler_undertitel)) || (lang === 'da'
    ? 'Vi deler viden og faglige artikler om autisme, udredning, behandling og relaterede emner.'
    : 'Vi delar kunskap och artiklar om autism, utredning, behandling och relaterade ämnen.');

  return (
    <section className={`bg-[#FCFDFC] py-16 md:py-20 px-6 font-sans border-t border-[#CCD7D2]/40 ${className}`}>
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <header className="mb-10 max-w-2xl">
          {/* Decorative Accent Rule */}
          <div className="w-8 h-[2px] bg-[#a3b8ad] rounded-full mb-4" />
          
          <h2 className="font-display text-3xl md:text-4xl text-[#173834] font-normal tracking-tight">
            {sectionTitle}
          </h2>
          <p className="mt-2 text-sm md:text-base text-[#61736F] leading-relaxed">
            {sectionSubtitle}
          </p>
        </header>

        {/* 3-Column Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {displayArticles.map((card, idx) => (
            <Link
              key={idx}
              to={localizePath(`/vidensbank/${card.slug}`)}
              className="group flex flex-col justify-between bg-white border border-[#E8ECE9] rounded-2xl p-6 sm:p-7 transition-all duration-200 hover:border-[#CBD5D0] hover:shadow-xs cursor-pointer"
            >
              <div>
                <h3 className="font-display text-xl sm:text-2xl text-[#173834] font-normal leading-snug group-hover:text-[#2C4E49] transition-colors mb-3">
                  {card.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#61736F] leading-relaxed">
                  {card.desc}
                </p>
              </div>

              {/* Bottom Right Arrow */}
              <div className="flex justify-end pt-6 mt-auto">
                <span className="text-[#173834] group-hover:text-[#2C4E49] group-hover:translate-x-1 transition-all duration-200">
                  <ArrowRight className="w-5 h-5 stroke-[1.75px]" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
